import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft, Save, X, Loader2, CreditCard, Check,
    AlertCircle, Calendar, User, Home, Receipt
} from 'lucide-react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { useToast } from '@/contexts/ToastContext';
import apiClient from '@/services/apiClient';

interface PaymentFormData {
    tenantId: string;
    unitId: string;
    propertyId: string;
    amount: number;
    paymentDate: string;
    paymentMethod: 'cash' | 'mpesa' | 'bank' | 'cheque' | 'other';
    reference: string;
    notes: string;
    invoiceId?: string;
}

const RecordPayment: React.FC = () => {
    const { propertyId } = useParams<{ propertyId?: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { success, error: toastError } = useToast();
    const { tenants, units, properties, fetchTenants, fetchInvoices, invoices } = useLandlordStore();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState<PaymentFormData>({
        tenantId: searchParams.get('tenantId') || '',
        unitId: searchParams.get('unitId') || '',
        propertyId: propertyId || searchParams.get('propertyId') || '',
        amount: 0,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'mpesa',
        reference: '',
        notes: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchTenants(),
                useLandlordStore.getState().fetchInvoices()
            ]);
        } catch (err) {
            console.error('Failed to load data:', err);
        } finally {
            setLoading(false);
        }
    };

    const selectedTenant = tenants.find(t => t.id === formData.tenantId);
    const selectedUnit = units.find(u => u.id === formData.unitId);

    // Get pending invoices for the selected tenant
    const pendingInvoices = invoices.filter(
        inv => inv.status === 'Pending' && 
        (formData.tenantId ? inv.tenant_name === selectedTenant?.full_name : true)
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.tenantId || !formData.amount || formData.amount <= 0) {
            toastError('Please fill in all required fields');
            return;
        }

        setSubmitting(true);
        try {
            // Find the unit for this tenant
            const tenantUnit = units.find(u => 
                u.current_tenant?.id === formData.tenantId || 
                u.current_tenant?.full_name === selectedTenant?.full_name
            );

            const paymentData = {
                tenantId: formData.tenantId,
                unitId: tenantUnit?.id || formData.unitId,
                propertyId: formData.propertyId,
                amount: formData.amount,
                paymentDate: formData.paymentDate,
                paymentMethod: formData.paymentMethod,
                reference: formData.reference,
                notes: formData.notes,
                invoiceId: formData.invoiceId
            };

            // Record payment via API
            await apiClient.post('/payments/record', paymentData);

            // Refresh invoices
            await fetchInvoices();

            success('Payment recorded successfully!');
            navigate('/landlord/financials');
        } catch (err: any) {
            console.error('Failed to record payment:', err);
            toastError(err.response?.data?.error || 'Failed to record payment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSelectInvoice = (invoiceId: string, amount: number) => {
        setFormData(prev => ({
            ...prev,
            invoiceId,
            amount
        }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/landlord/financials')}
                    className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                >
                    <ArrowLeft size={20} className="text-light-text dark:text-dark-text" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">Record Payment</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">
                        Manually record a tenant payment
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tenant Selection */}
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                    <h2 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Tenant Information</h2>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                Select Tenant <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                                <select
                                    value={formData.tenantId}
                                    onChange={(e) => setFormData(prev => ({ ...prev, tenantId: e.target.value }))}
                                    className="w-full pl-10 pr-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    required
                                >
                                    <option value="">Select a tenant...</option>
                                    {tenants
                                        .filter(t => t.status === 'Active')
                                        .map(tenant => (
                                            <option key={tenant.id} value={tenant.id}>
                                                {tenant.full_name} - Unit {tenant.unit_number}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>

                        {selectedTenant && (
                            <div className="flex items-center gap-4 p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                    <User size={24} className="text-primary" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-light-text dark:text-dark-text">{selectedTenant.full_name}</p>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                        Unit {selectedTenant.unit_number} • {selectedTenant.property_name}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="font-semibold text-light-text dark:text-dark-text">
                                        KES {units.find(u => u.id === selectedTenant.unit_number)?.rent_amount?.toLocaleString() || '0'}
                                    </p>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Monthly Rent</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Payment Details */}
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                    <h2 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Payment Details</h2>
                    
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Amount <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle">KES</span>
                                    <input
                                        type="number"
                                        value={formData.amount || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, amount: Number(e.target.value) }))}
                                        placeholder="0.00"
                                        className="w-full pl-10 pr-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        required
                                        min="1"
                                        step="0.01"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Payment Date <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                                    <input
                                        type="date"
                                        value={formData.paymentDate}
                                        onChange={(e) => setFormData(prev => ({ ...prev, paymentDate: e.target.value }))}
                                        className="w-full pl-10 pr-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Payment Method <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.paymentMethod}
                                    onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as any }))}
                                    className="w-full px-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                >
                                    <option value="mpesa">M-Pesa</option>
                                    <option value="cash">Cash</option>
                                    <option value="bank">Bank Transfer</option>
                                    <option value="cheque">Cheque</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Reference Number
                                </label>
                                <input
                                    type="text"
                                    value={formData.reference}
                                    onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                                    placeholder="e.g., M-Pesa code, cheque number"
                                    className="w-full px-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                Notes (Optional)
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Add any additional notes about this payment..."
                                rows={3}
                                className="w-full px-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                            />
                        </div>
                    </div>
                </div>

                {/* Pending Invoices */}
                {pendingInvoices.length > 0 && (
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <h2 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">
                            <Receipt size={20} className="inline mr-2" />
                            Apply to Invoice
                        </h2>
                        <div className="space-y-2">
                            {pendingInvoices.map(invoice => (
                                <button
                                    key={invoice.id}
                                    type="button"
                                    onClick={() => handleSelectInvoice(invoice.id, invoice.amount - invoice.amount_paid)}
                                    className={`w-full flex items-center justify-between p-4 rounded-lg border transition-colors ${
                                        formData.invoiceId === invoice.id
                                            ? 'border-primary bg-primary/5'
                                            : 'border-light-border dark:border-dark-border hover:bg-light-bg dark:hover:bg-dark-bg'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                            formData.invoiceId === invoice.id
                                                ? 'bg-primary text-white'
                                                : 'bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle'
                                        }`}>
                                            <Receipt size={18} />
                                        </div>
                                        <div className="text-left">
                                            <p className="font-medium text-light-text dark:text-dark-text">
                                                Rent - {invoice.unit_number}
                                            </p>
                                            <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                                Due: {new Date(invoice.due_date).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-light-text dark:text-dark-text">
                                            KES {(invoice.amount - invoice.amount_paid).toLocaleString()}
                                        </p>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                            {invoice.status}
                                        </p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Submit */}
                <div className="flex gap-4">
                    <button
                        type="button"
                        onClick={() => navigate('/landlord/financials')}
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors font-medium"
                    >
                        <X size={18} />
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Check size={18} />
                                Record Payment
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default RecordPayment;